import React, { useState, useEffect } from "react";
import Modal from "../../../../utils/components/Modal";
import Button from "../../../../utils/components/Button";
import Table from "../../../../utils/components/Table";

const PriceListTable = ({
    tournamentId,
    priceList = [],
    loading = {},
    savePriceItem,
    deletePriceItem,
    loadPriceList,
    loadingFetch,
}) => {
    const [showModal, setShowModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({
        sku: "",
        name: "",
        amount: "",
        type: "match-fee",
    });

    useEffect(() => {
        if (tournamentId) {
            loadPriceList(tournamentId);
        }
    }, [tournamentId]);

    const handleOpenModal = (item = null) => {
        if (item) {
            setEditingItem(item);
            setFormData({
                sku: item.sku,
                name: item.name,
                amount: item.amount ? Number(item.amount).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "",
                type: item.type || "custom-fee",
            });
        } else {
            setEditingItem(null);
            setFormData({
                sku: "",
                name: "",
                amount: "",
                type: "match-fee",
            });
        }
        setShowModal(true);
    };

    const handleSave = () => {
        let numericAmount = 0;
        if (formData.amount) {
            numericAmount = Number(String(formData.amount).replace(/\./g, "").replace(/,/g, "."));
        }

        savePriceItem({
            ...formData,
            id: editingItem?.id,
            price: numericAmount,
            description: formData.name,
            external_relationship_id: tournamentId,
        });
        setShowModal(false);
    };

    const addActions = (items) =>
        items.map((item) => ({
            ...item,
            formattedAmount: `$ ${Number(item.amount).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            actions: (
                <div className="btn-separated-group">
                    <Button
                        className="btn btn-warning btn-xs"
                        title={<i className="fa fa-pencil"></i>}
                        onClick={() => handleOpenModal(item)}
                    />
                    <Button
                        className="btn btn-danger btn-xs"
                        title={<i className="fa fa-trash"></i>}
                        withConfirmation={true}
                        confirmationTitle="Eliminar plantilla"
                        confirmationBody="¿Estás seguro que quieres eliminar esta plantilla de precio?"
                        onConfirmText="Eliminar"
                        onConfirm={() => deletePriceItem(item.id, tournamentId)}
                        loading={loading.deletePriceItem}
                    />
                </div>
            ),
        }));

    return (
        <div className="panel panel-default">
            <div className="panel-heading">
                <i className="fa fa-tags m-r-xs"></i> Lista de Precios
                <Button
                    title={
                        <span>
                            <i className="fa fa-plus"></i> Nueva Plantilla
                        </span>
                    }
                    className="btn btn-primary btn-xs pull-right"
                    onClick={() => handleOpenModal()}
                />
            </div>
            <div className="panel-body">
                <p className="text-muted small">
                    Define cargos recurrentes que podrás aplicar rápidamente a los equipos desde el panel financiero o la lista de lineups.
                </p>
                <Table
                    loading={loadingFetch}
                    headers={[
                        { label: "SKU", ref: "sku", className: "col-sm-2" },
                        { label: "Nombre", ref: "name", className: "col-sm-5" },
                        { label: "Monto", ref: "formattedAmount", className: "col-sm-2" },
                        { label: "Acciones", ref: "actions", className: "col-sm-3 text-center" },
                    ]}
                    rows={addActions(priceList)}
                    notFoundMessage="No hay plantillas configuradas para este torneo."
                />
            </div>

            <Modal
                show={showModal}
                handleShow={setShowModal}
                title={editingItem ? "Editar Plantilla" : "Nueva Plantilla"}
                buttons={
                    <div className="btn-separated-group">
                        <Button
                            className="btn btn-white"
                            title="Cancelar"
                            onClick={() => setShowModal(false)}
                        />
                        <Button
                            className="btn btn-primary"
                            title="Guardar"
                            onClick={handleSave}
                            loading={loading.savePriceItem}
                            disabled={!formData.sku || !formData.name || !formData.amount}
                        />
                    </div>
                }
                body={
                    <div>
                        <div className="form-group">
                            <label>SKU (Identificador único)</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Ej: DERECHO-PARTIDO"
                                value={formData.sku}
                                onChange={(e) => setFormData({ ...formData, sku: e.target.value.toUpperCase().replace(/\s/g, "-") })}
                                disabled={editingItem}
                            />
                        </div>
                        <div className="form-group">
                            <label>Nombre / Descripción</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Ej: Derecho de Partido"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            />
                        </div>
                        <div className="form-group">
                            <label>Monto ($)</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="0,00"
                                value={formData.amount}
                                onChange={(e) => {
                                    const val = e.target.value.replace(/[^0-9.,]/g, "");
                                    setFormData({ ...formData, amount: val });
                                }}
                                onBlur={(e) => {
                                    if (!formData.amount) return;
                                    let raw = formData.amount.replace(/\./g, "").replace(/,/g, ".");
                                    const num = Number(raw);
                                    if (!isNaN(num)) {
                                        setFormData({ ...formData, amount: num.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) });
                                    }
                                }}
                            />
                        </div>
                        <div className="form-group">
                            <label>Tipo de cargo</label>
                            <select
                                className="form-control"
                                value={formData.type}
                                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                            >
                                <option value="match-fee">Derecho a partido</option>
                                <option value="tournament-entry-fee">Inscripción</option>
                                <option value="sanction-fee">Sanción</option>
                                <option value="yellow-card-fee">Tarjeta amarilla</option>
                                <option value="red-card-fee">Tarjeta roja</option>
                                <option value="double-yellow-card-fee">Doble tarjeta amarilla</option>
                                <option value="protest-fee">Protesta</option>
                                <option value="social-club-fee">Club social</option>
                                <option value="custom-fee">Tipo personalizado</option>
                            </select>
                        </div>
                    </div>
                }
            />
        </div>
    );
};

export default PriceListTable;
